<template>
  <ReviewPanel @goto="$emit('goto', $event)">
    <template #leading-section>
      <EventReviewSection :title="dateTimeSectionTitle" />
    </template>

    <template #form-section="formSectionSlotProps">
      <ReviewSection :title="participantSectionTitle" icon="user-alt-filled">
        <sp-col
          sm="12"
          :md="participantContactMdCols(formSectionSlotProps)"
          :lg="participantContactLgCols(formSectionSlotProps)"
        >
          <sp-subtitle v-if="showContactSubtitle(formSectionSlotProps)">{{ contactHeading }}</sp-subtitle>
          <sp-subtitle>{{ displayName(formSectionSlotProps) }}</sp-subtitle>
          <span>{{ displayEmail(formSectionSlotProps) }}</span>
          <span>{{ displayPhone(formSectionSlotProps) }}</span>
        </sp-col>
        <sp-col
          v-if="hasAddressLines(formSectionSlotProps)"
          sm="12"
          :md="participantAddressMdCols()"
          :lg="participantAddressLgCols(formSectionSlotProps)"
        >
          <sp-subtitle v-if="addressHeading">{{ addressHeading }}</sp-subtitle>
          <div>{{ displayAddressStreet(formSectionSlotProps) }}</div>
          <div>{{ displayAddressCityLine(formSectionSlotProps) }}</div>
        </sp-col>
        <sp-col
          v-if="!hideReviewParticipantChange"
          sm="12"
          :md="participantChangeMdCols(formSectionSlotProps)"
          :lg="participantChangeLgCols()"
          align-items="end"
        >
          <sp-button variant="text-inline" @click="$emit('goto', 'form')">Change</sp-button>
        </sp-col>
      </ReviewSection>
    </template>
  </ReviewPanel>
</template>

<script setup>
import { computed, inject } from "vue";
import EventReviewSection from "./EventReviewSection.ce.vue";
import ReviewPanel from "./ReviewPanel.ce.vue";
import ReviewSection from "./ReviewSection.ce.vue";
import { t } from "#ce/components/SpCheckout/i18n";

function formData(slotProps) {
  return slotProps?.formData ?? {};
}

const hideReviewParticipantChangeInjected = inject("spCheckoutHideReviewParticipantChange", null);
const hideReviewParticipantChange = computed(() =>
  hideReviewParticipantChangeInjected ? hideReviewParticipantChangeInjected.value : false,
);

const contactHeading = computed(() => t("review_contact_heading"));
const dateTimeSectionTitle = computed(() => t("review_date_time_section_title"));
const participantSectionTitle = computed(() => t("review_participant_section_title"));
const addressHeading = computed(() => t("review_address_section_title"));

function displayName(slotProps) {
  const d = formData(slotProps);
  const first = d?.first_name ?? d?.firstName ?? "";
  const last = d?.last_name ?? d?.lastName ?? "";
  return `${first} ${last}`.trim();
}

function displayEmail(slotProps) {
  const d = formData(slotProps);
  return d?.email ?? d?.email_address ?? "";
}

function displayPhone(slotProps) {
  const d = formData(slotProps);
  return d?.phone ?? d?.phone_number ?? d?.mobile_phone ?? "";
}

function hasPrimaryContact(slotProps) {
  return Boolean(displayEmail(slotProps) || displayPhone(slotProps));
}

function showContactSubtitle(slotProps) {
  return Boolean(contactHeading.value && hasPrimaryContact(slotProps));
}

function displayAddressStreet(slotProps) {
  const d = formData(slotProps);
  return (
    d?.["address[street]"] ??
    d?.["address[street_address]"] ??
    d?.["address[streetAddress]"] ??
    d?.["address[line1]"] ??
    d?.address_street ??
    d?.street ??
    ""
  );
}

function displayAddressCityLine(slotProps) {
  const d = formData(slotProps);
  const city = d?.["address[city]"] ?? d?.["address[cityName]"] ?? d?.address_city ?? d?.city ?? "";
  const state = d?.["address[state]"] ?? d?.["address[stateCode]"] ?? d?.address_state ?? d?.state ?? "";
  const zip = d?.["address[zip_code]"] ?? d?.["address[zip]"] ?? d?.["address[zipCode]"] ?? d?.zip_code ?? d?.zip ?? "";
  return [city, state, zip].filter(Boolean).join(" ");
}

function hasAddressLines(slotProps) {
  return Boolean(displayAddressStreet(slotProps) || displayAddressCityLine(slotProps));
}

function participantShowChange() {
  return !hideReviewParticipantChange.value;
}

/** Medium+: contact + address as two columns when address exists; otherwise full or share row with Change. */
function participantContactMdCols(slotProps) {
  const addr = hasAddressLines(slotProps);
  if (addr) {
    return 6;
  }
  return participantShowChange() ? 10 : 12;
}

function participantContactLgCols(slotProps) {
  const addr = hasAddressLines(slotProps);
  const ch = participantShowChange();
  if (addr && ch) {
    return 5;
  }
  if (addr && !ch) {
    return 6;
  }
  if (!addr && ch) {
    return 10;
  }
  return 12;
}

function participantAddressMdCols() {
  return 6;
}

function participantAddressLgCols(slotProps) {
  return participantShowChange() && hasAddressLines(slotProps) ? 5 : 6;
}

function participantChangeMdCols(slotProps) {
  if (!participantShowChange()) {
    return undefined;
  }
  return hasAddressLines(slotProps) ? 12 : 2;
}

function participantChangeLgCols() {
  return participantShowChange() ? 2 : undefined;
}

defineEmits(["goto"]);
</script>
